import type { Capabilities, CameraDevice, FacingMode } from '@fastgarage/protocol';

/**
 * Photo capture + hashing helpers. ImageCapture and the torch/zoom constraint
 * extensions are not part of the standard TypeScript DOM lib, so they are
 * declared here in minimal form and always accessed defensively.
 */

export const JPEG_QUALITY = 0.92;

export const ALLOWED_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type UploadContentType = (typeof ALLOWED_CONTENT_TYPES)[number];

export interface ImageCaptureLike {
  takePhoto(): Promise<Blob>;
}
export type ImageCaptureCtor = new (track: MediaStreamTrack) => ImageCaptureLike;

/** Returns the ImageCapture constructor when the browser provides one. */
export function getImageCaptureCtor(scope: unknown = globalThis): ImageCaptureCtor | null {
  const ctor = (scope as { ImageCapture?: unknown }).ImageCapture;
  return typeof ctor === 'function' ? (ctor as ImageCaptureCtor) : null;
}

/** Hex-encoded SHA-256 of a blob via WebCrypto. */
export async function sha256Hex(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/** Pixel dimensions of an encoded image blob. */
export async function blobDimensions(blob: Blob): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(blob);
  try {
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}

/** Returns the blob's content type when the API accepts it, else null. */
export function normalizeContentType(blob: Blob): UploadContentType | null {
  return (ALLOWED_CONTENT_TYPES as readonly string[]).includes(blob.type)
    ? (blob.type as UploadContentType)
    : null;
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob returned null'));
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}

/** Re-encode an arbitrary image blob (e.g. HEIC from a native dialog) to JPEG. */
export async function reencodeToJpeg(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    ctx.drawImage(bitmap, 0, 0);
    return await canvasToJpeg(canvas);
  } finally {
    bitmap.close();
  }
}

/**
 * Canvas still frame from a playing <video> element bound to the local
 * camera stream, at the video track's current resolution (TAKE_PHOTO fallback
 * when ImageCapture is unavailable).
 */
export async function captureCanvasFrame(
  track: MediaStreamTrack,
  video: HTMLVideoElement,
): Promise<Blob> {
  const settings = track.getSettings();
  const width = settings.width ?? video.videoWidth;
  const height = settings.height ?? video.videoHeight;
  if (!width || !height) throw new Error('video dimensions unavailable');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');
  ctx.drawImage(video, 0, 0, width, height);
  return canvasToJpeg(canvas);
}

/** Full-resolution photo via ImageCapture (Android/Chrome). */
export async function captureViaImageCapture(track: MediaStreamTrack): Promise<Blob> {
  const Ctor = getImageCaptureCtor();
  if (!Ctor) throw new Error('ImageCapture unavailable');
  return new Ctor(track).takePhoto();
}

// ── Torch / zoom (non-standard constraint extensions) ───────────────────────

interface ExtendedCapabilities extends MediaTrackCapabilities {
  torch?: boolean;
  zoom?: { min?: number; max?: number; step?: number };
}

interface ExtendedConstraintSet extends MediaTrackConstraintSet {
  torch?: boolean;
  zoom?: number;
}

export async function applyTorch(track: MediaStreamTrack, on: boolean): Promise<void> {
  const advanced: ExtendedConstraintSet[] = [{ torch: on }];
  await track.applyConstraints({ advanced });
}

export async function applyZoom(track: MediaStreamTrack, zoom: number): Promise<void> {
  const advanced: ExtendedConstraintSet[] = [{ zoom }];
  await track.applyConstraints({ advanced });
}

export interface TrackHardwareCapabilities {
  torch: boolean;
  zoom: { min: number; max: number; step?: number } | null;
}

/** Reads torch/zoom support from the live video track (defensively — Firefox
 * has no getCapabilities at all). */
export function readTrackCapabilities(track: MediaStreamTrack): TrackHardwareCapabilities {
  let caps: ExtendedCapabilities = {};
  try {
    caps = (track.getCapabilities?.() ?? {}) as ExtendedCapabilities;
  } catch {
    // Treat as no hardware extras.
  }
  const zoom =
    caps.zoom && typeof caps.zoom.min === 'number' && typeof caps.zoom.max === 'number'
      ? { min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step }
      : null;
  return { torch: caps.torch === true, zoom };
}

// ── Platform / facing-mode detection for CAPABILITIES ───────────────────────

export function detectPlatform(
  ua: string = navigator.userAgent,
  maxTouchPoints: number = navigator.maxTouchPoints ?? 0,
): Capabilities['platform'] {
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  // iPadOS pretends to be a Mac but has a touch screen.
  if (/macintosh/i.test(ua) && maxTouchPoints > 1) return 'ios';
  if (/windows|macintosh|linux|cros|x11/i.test(ua)) return 'desktop';
  return 'other';
}

// ── Camera enumeration (built-in + external USB/UVC) ───────────────────────

/** enumerateDevices entries expose getCapabilities() only as InputDeviceInfo. */
interface InputDeviceInfoLike extends MediaDeviceInfo {
  getCapabilities?: () => MediaTrackCapabilities;
}

/** Hard limits from `cameraDeviceSchema` / `capabilitiesSchema`. */
const MAX_DEVICE_ID_LENGTH = 256;
const MAX_LABEL_LENGTH = 200;
const MAX_CAMERAS = 32;

/** Minimal slice of navigator.mediaDevices used here — injectable for tests. */
export interface MediaDeviceSource {
  enumerateDevices(): Promise<MediaDeviceInfo[]>;
}

function labelFacing(label: string): FacingMode | null {
  if (/front|selfie|user facing|vorne|frontkamera/i.test(label)) return 'user';
  if (/back|rear|environment|hinten|rückkamera|ruckkamera/i.test(label)) return 'environment';
  return null;
}

/** Facing of an enumerated device: the browser's capability first, then a
 * label heuristic. External USB cameras legitimately end up as null. */
function deviceFacing(device: InputDeviceInfoLike): FacingMode | null {
  let announced: unknown;
  try {
    announced = device.getCapabilities?.().facingMode;
  } catch {
    // Safari throws for devices without an active track — heuristic below.
  }
  const modes = Array.isArray(announced) ? announced : [];
  if (modes.includes('environment')) return 'environment';
  if (modes.includes('user')) return 'user';
  return labelFacing(device.label ?? '');
}

/**
 * All selectable video inputs, including external USB/UVC cameras. Labels are
 * only populated once camera permission was granted, so this must run after
 * the first getUserMedia — unnamed entries get a positional fallback label.
 *
 * The live track (when passed) refines the entry it belongs to: its settings
 * carry the authoritative facing mode for the camera actually in use.
 */
export async function enumerateCameras(
  activeTrack: MediaStreamTrack | null = null,
  source?: MediaDeviceSource,
): Promise<CameraDevice[]> {
  const mediaDevices =
    source ?? (typeof navigator === 'undefined' ? undefined : navigator.mediaDevices);
  let devices: MediaDeviceInfo[] = [];
  try {
    devices = (await mediaDevices?.enumerateDevices()) ?? [];
  } catch {
    return []; // enumerateDevices unavailable/blocked — no picker, no crash.
  }
  const activeSettings = activeTrack?.getSettings?.() ?? {};
  const activeId = typeof activeSettings.deviceId === 'string' ? activeSettings.deviceId : null;
  const activeFacing = activeSettings.facingMode;

  const seen = new Set<string>();
  const cameras: CameraDevice[] = [];
  for (const device of devices) {
    // Limits mirror cameraDeviceSchema — an over-long id or label would fail
    // validation and drop the whole CAPABILITIES message.
    if (
      device.kind !== 'videoinput' ||
      !device.deviceId ||
      device.deviceId.length > MAX_DEVICE_ID_LENGTH ||
      seen.has(device.deviceId) ||
      cameras.length >= MAX_CAMERAS
    ) {
      continue;
    }
    seen.add(device.deviceId);
    const isActive = activeId !== null && device.deviceId === activeId;
    const trackFacing =
      isActive && (activeFacing === 'user' || activeFacing === 'environment') ? activeFacing : null;
    cameras.push({
      deviceId: device.deviceId,
      label: device.label?.trim().slice(0, MAX_LABEL_LENGTH) || `Kamera ${cameras.length + 1}`,
      facingMode: trackFacing ?? deviceFacing(device as InputDeviceInfoLike),
    });
  }
  return cameras;
}

/** deviceId of the camera a track is bound to, null when unknown. Over-long
 * ids are treated as unknown — capabilitiesSchema would reject the whole
 * message rather than just the field. */
export function trackDeviceId(track: MediaStreamTrack | null): string | null {
  const id = track?.getSettings?.().deviceId;
  return typeof id === 'string' && id.length > 0 && id.length <= MAX_DEVICE_ID_LENGTH ? id : null;
}

/**
 * Facing modes reachable via SWITCH_CAMERA. Whatever the cameras announce
 * decides: a camera without a facing mode (typically an external USB one) is
 * no evidence for a second facing. Only when nothing announces anything does
 * the old heuristic apply (more than one camera → front and back exist), so
 * browsers that hide facing modes entirely keep their switch button.
 */
export async function detectFacingModes(
  track: MediaStreamTrack,
  cameras?: CameraDevice[],
): Promise<FacingMode[]> {
  const list = cameras ?? (await enumerateCameras(track));
  const announced = new Set<FacingMode>();
  for (const camera of list) {
    if (camera.facingMode) announced.add(camera.facingMode);
  }
  if (announced.size > 1) return ['user', 'environment'];
  if (announced.size === 1) return [...announced];
  if (list.length > 1) return ['user', 'environment'];
  const facing = track.getSettings().facingMode;
  return facing === 'user' || facing === 'environment' ? [facing] : ['environment'];
}
